/**
 * Exportación de listas a Excel (XLSX).
 * Usado por las páginas ListView para la acción masiva "Exportar datos".
 *
 * PERFORMANCE: xlsx es pesado (~600KB). Lo importamos lazy (dynamic import)
 * para que NO afecte el bundle inicial — solo se descarga cuando el founder
 * hace click en "Exportar Excel". Patrón vendor split estándar.
 */

import { format } from 'date-fns';
import type { ListViewColumn } from '@/types/listView.types';

function getCellValue(column: ListViewColumn, item: Record<string, unknown>): string {
  if (column.type === 'actions') return '';

  const key = column.key;
  const value = item[key];

  if (column.type === 'badge') {
    if ('format' in column && typeof column.format === 'function') {
      return String(column.format(value, item) ?? '');
    }
    if ('map' in column && column.map && value !== undefined && value !== null) {
      const mapped = column.map[String(value)];
      return mapped != null ? String(mapped) : String(value);
    }
  }

  if (column.type === 'number' && 'format' in column && typeof column.format === 'function') {
    return String(column.format(value as number, item) ?? '');
  }

  if (column.type === 'thumbnail') {
    const imageKey = 'imageKey' in column ? column.imageKey : undefined;
    const emptyKey = 'emptyKey' in column ? column.emptyKey : undefined;
    const url = imageKey ? item[imageKey] : undefined;
    const fallback = emptyKey ? item[emptyKey] : undefined;
    if (url != null && url !== '') return String(url);
    if (fallback != null && fallback !== '') return String(fallback);
    return '';
  }

  if (column.type === 'thumbnailWithText') {
    const textKey = 'textKey' in column ? column.textKey : undefined;
    const imageKey = 'imageKey' in column ? column.imageKey : undefined;
    if (textKey && item[textKey] != null) return String(item[textKey]);
    if (imageKey && item[imageKey] != null) return String(item[imageKey]);
    return '';
  }

  if (value == null) return '';
  return String(value);
}

/**
 * Exporta una lista de ítems a un archivo Excel (.xlsx).
 * Usa las columnas de la config de ListView (excluye tipo "actions") para cabeceras y valores.
 *
 * Lazy load xlsx (~600KB) — solo se descarga al primer click de exportación.
 */
export async function exportListToExcel(params: {
  columns: ListViewColumn[];
  items: Record<string, unknown>[];
  filename: string;
}): Promise<void> {
  const { columns, items, filename } = params;

  const exportColumns = columns.filter((c) => c.type !== 'actions');
  if (exportColumns.length === 0) return;

  const rows = items.map((item) => {
    const row: Record<string, string> = {};
    for (const col of exportColumns) {
      row[col.title] = getCellValue(col, item);
    }
    return row;
  });

  // Dynamic import — code-split de xlsx fuera del bundle principal.
  const XLSX = await import('xlsx');
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
  const finalFilename = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  XLSX.writeFile(workbook, finalFilename);
}

/** Genera nombre de archivo para exportación: "Título-2025-03-12-1430.xlsx" */
export function getExportFilename(pageTitle: string): string {
  const safe = pageTitle.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '');
  return `${safe}-${format(new Date(), 'yyyy-MM-dd-HHmm')}.xlsx`;
}
